import { FieldErrors, UseFormRegister, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Input } from "src/components/Form/input";
import { Select } from "src/components/Form/select";
import { CreateCreditVouchers } from "../../types";
import { ProjectType } from "src/features/organizations/types/organization.entity";

type VoucherFormFieldsProps = {
  register: UseFormRegister<CreateCreditVouchers>;
  errors: FieldErrors<CreateCreditVouchers>;
  organizations?: Array<{ id: string; name: string }>;
  projects?: Array<{ id: string; name: string; projectType: ProjectType; dateRange?: { start: any; end: any } }>;
  selectedProjectId?: string;
  isAmountDisabled?: boolean;
  voucherType?: string;
  watch: UseFormWatch<CreateCreditVouchers>;
  setValue: UseFormSetValue<CreateCreditVouchers>;
};
export const VouchersFormFields = ({ register, errors, organizations, projects, selectedProjectId, isAmountDisabled, voucherType, watch, setValue }: VoucherFormFieldsProps) => {
  // Find the selected project to check its type
  const selectedProject = projects?.find(project => project.id === selectedProjectId);
  const isPrivateMode = selectedProject?.projectType === "PRIVATE_MODE" || selectedProject?.projectType === "PRIVATE_MODE_TOP_UP";
  const isPremium = voucherType === "PREMIUM";
  const isMonthlyPremium = voucherType === "MONTHLY_PREMIUM";
  const isYearlyPremium = voucherType === "YEARLY_PREMIUM";
  const isPremiumType = isPremium || isMonthlyPremium || isYearlyPremium;
  const organizationId = watch("organizationId");
  const useProjectEndDate = watch("useProjectEndDate");
  const linkToProjectTermination = watch("linkToProjectTermination");
  // Disable subscriptionEndDate only when checkbox is checked (for PREMIUM type)
  const isSubscriptionEndDateDisabled = isPremium && !!useProjectEndDate;
  
  // Calculate minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];
  
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-xl font-semibold text-gray-800">Voucher Details</h3>
        </div>
        <p className="text-gray-600 text-sm">Configure your credit voucher settings</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-1">
          <Select 
            label="Voucher Type"
            options={["PROMO", "TOP_UP", "PRIVATE_MODE", "PREMIUM", "MONTHLY_PREMIUM", "YEARLY_PREMIUM"]} 
            registration={register("type")} 
            error={errors.type?.message}
            disabled={isAmountDisabled || isPrivateMode}
            className="w-full"
          />
          {isPrivateMode && (
            <div className="flex items-center mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-xs text-amber-700 font-medium">Voucher type is automatically set to PRIVATE_MODE for private mode projects</p>
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          <Select
            label="Currency"
            options={["KARMA", "EUR"]}
            registration={register("currency")}
            error={errors.currency?.message}
            disabled={isAmountDisabled || isPrivateMode || isPremiumType}
            className="w-full"
          />
          {isPrivateMode && (
            <div className="flex items-center mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-xs text-amber-700 font-medium">Currency is automatically set to KARMA for private mode projects</p>
            </div>
          )}
          {isPremiumType && (
            <div className="flex items-center mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-blue-700 font-medium">Currency is automatically set to KARMA for PREMIUM vouchers</p>
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
          <Input 
            registration={register("amount")} 
            type="number" 
            step="any"
            error={errors.amount?.message}
            disabled={isAmountDisabled || isPrivateMode || isPremiumType}
            placeholder="Enter amount"
            className="w-full"
          />
          {isPrivateMode && (
            <div className="flex items-center mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
              <svg className="w-4 h-4 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-xs text-amber-700 font-medium">Amount is automatically set to 0 for private mode projects</p>
            </div>
          )}
          {isPremiumType && (
            <div className="flex items-center mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-blue-700 font-medium">Amount is automatically set to 0 for PREMIUM vouchers</p>
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Count (optional)</label>
          <Input 
            registration={register("count")} 
            type="number" 
            error={errors.count?.message}
            placeholder="Number of vouchers"
            className="w-full"
          />
        </div>
        
        <div className="space-y-1 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Expiry Date (optional)</label>
          <Input
            registration={register("expiryDate")}
            type="Date"
            error={errors.expiryDate?.message}
            className="w-full"
          />
        </div>
        
        {isPremium && (
          <div className="space-y-1 md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subscription End Date {isPremium && !useProjectEndDate && <span className="text-red-500">*</span>}
            </label>
            <Input
              registration={register("subscriptionEndDate")}
              type="Date"
              error={errors.subscriptionEndDate?.message}
              disabled={isSubscriptionEndDateDisabled}
              min={minDate}
              className="w-full"
              inputClassName={isSubscriptionEndDateDisabled ? '!bg-blue-50' : ''}
            />
            {selectedProjectId && (
              <div className="mt-3 space-y-3">
                <label className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl cursor-pointer transition-all hover:shadow-md">
                  <input
                    type="checkbox"
                    {...register("useProjectEndDate")}
                    className="w-5 h-5 mr-3 text-blue-500 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">Use project end date</span>
                  </div>
                </label>
                <label className="flex items-center p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl cursor-pointer transition-all hover:shadow-md">
                  <input
                    type="checkbox"
                    {...register("linkToProjectTermination")}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setValue("linkToProjectTermination", checked);
                      // If checked, automatically check useProjectEndDate
                      if (checked) {
                        setValue("useProjectEndDate", true);
                      }
                    }}
                    className="w-5 h-5 mr-3 text-purple-500 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-semibold text-gray-700">Link to project termination</span>
                  </div>
                </label>
              </div>
            )}
            {isSubscriptionEndDateDisabled && useProjectEndDate && (
              <div className="flex items-center mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-700 font-medium">Subscription end date is automatically set to the project's end date</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center mb-4">
          <svg className="w-5 h-5 text-indigo-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h4 className="text-lg font-medium text-gray-800">Organization & Project</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1">
            <Select
              label="Organization (optional)"
              includeOption="None"
              options={organizations ?? []}
              registration={register("organizationId")}
              getOptionValue={(org: { id: string; name: string }) => org.id}
              getOptionLabel={(org: { id: string; name: string }) => org.name}
              error={errors.organizationId?.message}
              className="w-full"
            />
            {isPremiumType && (
              <div className="flex items-start mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm text-blue-800 leading-relaxed">Premium vouchers are only applicable to voucher or vouchers and hours registerations projects.</p>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <Select
              label="Project (optional)"
              options={projects ?? []}
              registration={register("projectId")}
              getOptionValue={(project: { id: string; name: string; projectType: ProjectType }) => project.id}
              getOptionLabel={(project: { id: string; name: string; projectType: ProjectType }) => project.name}
              includeOption="None"
              error={errors.projectId?.message}
              className="w-full"
            />
            {isPrivateMode && (
              <div className="flex items-center mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-700 font-medium">Private mode project selected - amount and currency are auto-configured</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
